import React, { useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Info, Check, AlertTriangle } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * COMMONS ACTIVATION — DUAL STEWARD RULE
 * 
 * Per FINAL LOCKED DIRECTIVE:
 * - Convert a Group to Commons (requires Steward tier)
 * - MUST assign an Assistant Steward at activation
 * - Cannot activate without both stewards
 */
export default function CommonsActivation() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [assistantUsername, setAssistantUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState({
    dualApproval: false,
    renewal: false,
    governance: false
  });

  const allAccepted = Object.values(accepted).every(v => v);

  const activateCommons = async () => {
    if (!assistantUsername.trim()) {
      toast.error('Assistant Steward username is required');
      return;
    }

    if (!allAccepted) {
      toast.error('Please accept all governance rules');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/commons/activate`, {
        group_id: groupId,
        assistant_steward_username: assistantUsername.trim().replace('@', '')
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Commons activated successfully!');
      navigate(`/commons/${res.data.commons_id}/manage`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate Commons');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:opacity-70 transition-opacity">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Activate Commons</h1>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Info Banner */}
        <Card className="p-4" style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}>
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-1">Converting to Commons</h3>
              <p className="text-sm opacity-90">
                Commons are stewarded, non-expiring groups. They require dual stewardship for governance.
              </p>
            </div>
          </div>
        </Card>

        {/* Dual Steward Requirement */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
            <Users className="w-5 h-5" style={{ color: 'var(--lk-terra)' }} />
            Dual Steward Requirement
          </h3>
          
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Every Commons must have both a Primary Steward (you) and an Assistant Steward. 
            This ensures shared governance and prevents unilateral irreversible actions.
          </p>

          <div className="space-y-3">
            {/* Primary Steward (current user) */}
            <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-sage)' }}>
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>@{user?.username}</p>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Primary Steward (You)</p>
              </div>
              <Check className="w-5 h-5 ml-auto" style={{ color: 'var(--lk-sage)' }} />
            </div>

            {/* Assistant Steward (to be assigned) */}
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-terra)' }}>
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>Assistant Steward</p>
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Required for activation</p>
                </div>
              </div>
              
              <input
                type="text"
                value={assistantUsername}
                onChange={(e) => setAssistantUsername(e.target.value)}
                placeholder="@username of a group member"
                className="w-full p-2 rounded-lg"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              />
              <p className="text-xs mt-2" style={{ color: 'var(--fy-text-disabled)' }}>
                Must be an existing member of this group
              </p>
            </div>
          </div>
        </Card>

        {/* Governance Acknowledgments */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            Governance Acknowledgment
          </h3>
          
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted.dualApproval}
                onChange={(e) => setAccepted(prev => ({ ...prev, dualApproval: e.target.checked }))}
                className="mt-1"
              />
              <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                I understand that <strong>irreversible actions</strong> (delete, archive, sponsor removal, export) 
                require approval from <strong>both stewards</strong>.
              </span>
            </label>
            
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted.renewal}
                onChange={(e) => setAccepted(prev => ({ ...prev, renewal: e.target.checked }))}
                className="mt-1"
              />
              <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                I understand that Commons renew <strong>annually</strong>. Non-renewal results in the Commons 
                becoming <strong>read-only</strong> (archive preserved).
              </span>
            </label>
            
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted.governance}
                onChange={(e) => setAccepted(prev => ({ ...prev, governance: e.target.checked }))}
                className="mt-1"
              />
              <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                I accept responsibility as <strong>Primary Steward</strong> and will share governance 
                with my Assistant Steward in good faith.
              </span>
            </label>
          </div>
        </Card>

        {/* Warning */}
        <Card className="p-4" style={{ backgroundColor: 'rgba(194, 106, 81, 0.1)', border: '1px solid var(--lk-terra)' }}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--lk-terra)' }} />
            <p className="text-sm" style={{ color: 'var(--lk-terra)' }}>
              Once converted to Commons, this group will no longer expire. 
              You cannot convert back to a regular group.
            </p>
          </div>
        </Card>

        {/* Activate Button */}
        <Button
          onClick={activateCommons}
          disabled={submitting || !assistantUsername.trim() || !allAccepted}
          className="w-full"
          style={{ backgroundColor: 'var(--lk-sage)' }}
        >
          {submitting ? 'Activating...' : 'Activate Commons'}
        </Button>
      </div>
    </div>
  );
}
